import { useContext, useEffect, useState } from 'react';
import CheckoutConfigRenderer from '../components/builder/CheckoutConfigRenderer';
import CheckoutComponent from '../components/checkout/CheckoutComponent';
// import Loading from '../components/common/Loading';
import ProductPurchaseComponent from '@/components/checkout/ProductPurchaseComponent';
import { getQueryParams } from '@/utils/url';
import CreditCardSuccessPayment from '../components/payment/SuccessPayment';
import WaitingPayment from '../components/payment/WaitingPayment';
import { CheckoutContext } from '../contexts/CheckoutContext';
import useDevice from '../hooks/useDevice';
import useSettings from '../hooks/useSettings';
import ErrorPage from './ErrorPage';
import { trackPageView } from '@/utils/analytics';
import posthog from 'posthog-js';

// import { COMPANY_ERROR_STATUSES, PRODUCT_ERROR_STATUSES } from '../constants/errors'

const CheckoutPage = () => {
  const { offer, isFetching, firstPayment, countdownHeight } = useContext(CheckoutContext);

  const { device } = useDevice();

  const [timeoutError, setTimeoutError] = useState(false);

  const settings = useSettings();

  const style = {
    backgroundColor: settings?.backgroundColor,
    backgroundImage: settings?.backgroundImage,
    backgroundSize: settings?.backgroundSize,
    backgroundPosition: settings?.backgroundPosition,
    backgroundRepeat: settings?.backgroundRepeat
  };

  // Track page view when component mounts
  useEffect(() => {
    trackPageView(window.location.pathname);
  }, []);

  // Redirect to upsell page
  useEffect(() => {
    const currentUrl = window.location.href;
    const queryParams = getQueryParams(currentUrl);
    const redirect = offer?.product?.upsell;
    const skipErrors = offer?.product.redirectUpsellWithBumpFail;
    const paymentHasErrors = firstPayment?.errors?.length;
    const paid = firstPayment?.status === 'paid';

    if (!redirect || !paid) return;

    if ((skipErrors || !paymentHasErrors) && offer?.product?.upsellPage?.length) {
      const upsellUrl = new URL(offer?.product?.upsellPage);

      Object.keys(queryParams).forEach((key) => {
        upsellUrl.searchParams.append(key, queryParams[key]);
      });

      posthog.capture('upsell_redirect', {
        product_id: offer.product.short_id,
        product_name: offer.product.name,
        upsell_url: upsellUrl.toString()
      });

      window.location.href = upsellUrl.toString();
    }
  }, [
    firstPayment,
    offer?.product?.upsellPage,
    offer?.product?.upsell,
    offer?.product?.redirectUpsellWithBumpFail
  ]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isFetching) {
        setTimeoutError(true);
      }
    }, 30000);

    return () => {
      clearTimeout(timeout);
    };
  }, [isFetching]);

  if (isFetching)
    return (
      <div className="flex flex-col min-h-screen w-full " style={style}>
        <div className="max-w-[1080px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="col-span-1 lg:col-span-2 px-3 py-5">
              <CheckoutComponent />
            </div>

            <div className="col-span-1 lg:block pt-5 hidden">
              <ProductPurchaseComponent
                isLoading={isFetching}
                price={offer?.price || 0}
                product={offer?.product as any}
                sellerEmail={offer?.sellerEmail || ''}
                type={offer?.type || ''}
                recurrencePeriod={offer?.recurrence_period}
                paymentMethods={offer?.product.paymentMethods}
              />
            </div>
          </div>
        </div>
      </div>
    );

  if (timeoutError) {
    return <ErrorPage />;
  }

  const companyErrorStatuses = ['blocked'];
  const productErrorStatuses = ['active'];

  if (
    companyErrorStatuses.includes(offer?.companyStatus as string) ||
    !productErrorStatuses.includes(offer?.product?.status || '') ||
    offer?.affiliate?.companyStatus === 'blocked'
  ) {
    return <ErrorPage />;
  }
  //@ts-ignore
  const { checkout } = offer;

  if (firstPayment?.status === 'redo_payment') {
    return (
      <div style={style} className="flex p-2 md:p-5 w-full justify-center">
        <div className="flex justify-center w-[1080px] max-w-full">
          {checkout.config ? (
            <CheckoutConfigRenderer config={checkout.config} device={device} />
          ) : (
            <CheckoutComponent />
          )}
        </div>
      </div>
    );
  }

  if (firstPayment?.status === 'waiting_payment') {
    return (
      <div
        style={style}
        className="bg-[#161c24] flex flex-col items-center justify-center min-h-screen w-full md:py-5 md:py-5 md:px-12"
      >
        <div className="mx-auto max-w-[720px]">
          <WaitingPayment />
        </div>
      </div>
    );
  }

  if (firstPayment?.status === 'paid') {
    return (
      <div style={style} className="flex justify-center p-2 md:p-5 w-full">
        <div className="max-sm:w-full w-[720px]">
          <CreditCardSuccessPayment />
        </div>
      </div>
    );
  }

  if (!checkout?.config) {
    return (
      <div
        style={style}
        className="bg-[#161c24] flex flex-col min-h-screen w-full md:py-5 md:py-5 md:px-12"
      >
        {/* {(COMPANY_ERROR_STATUSES.includes(offer?.companyStatus)
          || PRODUCT_ERROR_STATUSES.includes(offer?.product.status || ''))
          && <ErrorTopAlert message={modalErrorMessage} />} */}

        <div className="max-w-[1080px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="col-span-1 lg:col-span-2 px-3">
              <CheckoutComponent />
            </div>
            <div className="col-span-1 hidden lg:block">
              <ProductPurchaseComponent
                isLoading={isFetching}
                price={offer?.price || 0}
                product={offer?.product as any}
                sellerEmail={offer?.sellerEmail || ''}
                type={offer?.type || ''}
                recurrencePeriod={offer?.recurrence_period}
                paymentMethods={offer?.product.paymentMethods}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const desktopRows = checkout?.config?.desktop?.rows;

  const lastDesktopRows = desktopRows[desktopRows.length - 1]?.layout;

  if (JSON.stringify(lastDesktopRows) === '[12]' && device === 'desktop') {
    return (
      <div
        className="bg-[#161c24] flex flex-col min-h-screen w-full py-4 md:py-5 md:px-12"
        style={style}
      >
        {/* {(COMPANY_ERROR_STATUSES.includes(offer?.companyStatus)
        || PRODUCT_ERROR_STATUSES.includes(offer?.product.status || ''))
        && <ErrorTopAlert message={modalErrorMessage} />} */}
        <div
          className="max-w-[1080px] mx-auto"
          style={{
            marginTop: countdownHeight - 10
          }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="col-span-1 lg:col-span-2 pt-0">
              <CheckoutConfigRenderer config={checkout.config} device={device} />
            </div>

            <div className="col-span-1 py-3 hidden lg:block pt-3">
              <ProductPurchaseComponent
                isLoading={isFetching}
                price={offer?.price || 0}
                product={offer?.product as any}
                sellerEmail={offer?.sellerEmail || ''}
                type={offer?.type || ''}
                recurrencePeriod={offer?.recurrence_period}
                paymentMethods={offer?.product.paymentMethods}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-[#161c24] flex flex-col min-h-screen w-full md:py-5 md:py-5 md:px-12"
      style={style}
    >
      {/* {(COMPANY_ERROR_STATUSES.includes(offer?.companyStatus)
        || PRODUCT_ERROR_STATUSES.includes(offer?.product.status || ''))
        && <ErrorTopAlert message={modalErrorMessage} />} */}
      <div
        className="max-w-[1080px] mx-auto"
        style={{
          marginTop: countdownHeight - 10
        }}
      >
        <CheckoutConfigRenderer config={checkout.config} device={device} />
      </div>
    </div>
  );
};

export default CheckoutPage;
